/**
 * BodyCompositionTwinPage — iter612
 * DEXA-style body composition: Navy body fat % + VAI proxy + lean mass.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Activity, Save } from "lucide-react";
import { toast } from "sonner";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";

import { MobileTopBar } from "@/components/MobileTopBar";
export default function BodyCompositionTwinPage() {
  const { API, user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    weight_kg: "", height_cm: "", waist_cm: "",
    hip_cm: "", neck_cm: "", age: "",
    sex: user?.profile?.personal?.sex || "male",
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/body-composition-twin/snapshot`);
      setSnapshot(data);
      if (data.current) {
        setForm((f) => ({
          ...f,
          weight_kg: data.current.weight_kg ?? f.weight_kg,
          height_cm: data.current.height_cm ?? f.height_cm,
          waist_cm: data.current.waist_cm ?? f.waist_cm,
          hip_cm: data.current.hip_cm ?? f.hip_cm,
          neck_cm: data.current.neck_cm ?? f.neck_cm,
          age: data.current.age ?? f.age,
          sex: data.current.sex || f.sex,
        }));
      }
      try {
        const r = await axios.post(`${API}/body-composition-twin/insights`, {}, { timeout: 30000 });
        setInsight(r.data.insight_bg || "");
      } catch {}
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        weight_kg: parseFloat(form.weight_kg),
        height_cm: parseFloat(form.height_cm),
        waist_cm: parseFloat(form.waist_cm),
        hip_cm: form.hip_cm ? parseFloat(form.hip_cm) : null,
        neck_cm: form.neck_cm ? parseFloat(form.neck_cm) : null,
        age: form.age ? parseInt(form.age) : null,
        sex: form.sex,
      };
      await axios.post(`${API}/body-composition-twin/snapshot`, payload);
      toast.success("Измерването е записано");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    }
    setSaving(false);
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const curr = snapshot?.current;
  const delta = snapshot?.delta;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="body-comp-twin-page">
      <MobileTopBar title="Телесен състав" fallbackTo="/dashboard" testid="body_comp-twin-mobile-topbar" />
      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
    </div>;
  }

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="body-comp-twin-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* iter1017 — профилът захранва близнака */}
        <ProfileContextStrip domain="body" isDark={isDark} className="mb-4" />
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold">💪 Телесен състав Twin (DEXA-стил)</h1>
        </div>

        {curr && (
          <div className={`p-4 rounded-2xl border mb-4 ${cardCls}`} data-testid="bc-snapshot">
            <div className="text-[10px] uppercase opacity-50 mb-2">Текуща оценка</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Tile label="Body Fat" value={curr.body_fat_pct} unit="%" band={curr.bf_band} delta={delta?.body_fat_pct} isDark={isDark} testid="bc-body-fat" />
              <Tile label="Lean mass" value={curr.lean_mass_kg} unit="kg" delta={delta?.lean_mass_kg} isDark={isDark} testid="bc-lean" />
              <Tile label="Висцерален (VAI)" value={curr.vai_score} band={curr.vai_band} delta={delta?.vai_score} isDark={isDark} testid="bc-vai" />
              <Tile label="BMI" value={curr.bmi} isDark={isDark} testid="bc-bmi" />
            </div>
            <div className="flex gap-4 mt-3 text-xs opacity-70">
              <div>WHR: <b>{curr.whr ?? "—"}</b></div>
              <div>WHtR: <b>{curr.whtr}</b></div>
              <div>Тегло: <b>{curr.weight_kg}kg</b></div>
            </div>
            {insight && (
              <div className={`mt-3 p-3 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-amber-50"}`} data-testid="bc-insight">
                <div className="text-[10px] uppercase opacity-50 mb-1">✨ AI препоръка</div>
                <p className="text-sm whitespace-pre-wrap">{insight}</p>
              </div>
            )}
          </div>
        )}

        <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="bc-form">
          <h2 className="font-bold text-sm mb-3">{curr ? "Ново измерване" : "Първо измерване"}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { id: "weight_kg", label: "Тегло (kg) *" },
              { id: "height_cm", label: "Височина (cm) *" },
              { id: "waist_cm", label: "Талия (cm) *" },
              { id: "hip_cm", label: "Ханш (cm)" },
              { id: "neck_cm", label: "Шия (cm)" },
              { id: "age", label: "Възраст (г)" },
            ].map((f) => (
              <div key={f.id}>
                <Label className="text-xs">{f.label}</Label>
                <Input type="number" step="0.1" value={form[f.id]} onChange={(e) => setForm({ ...form, [f.id]: e.target.value })} data-testid={`bc-input-${f.id}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs">Пол</Label>
              <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })} className={`w-full h-9 px-2 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} data-testid="bc-input-sex">
                <option value="male">Мъж</option>
                <option value="female">Жена</option>
              </select>
            </div>
          </div>
          <Button onClick={save} disabled={saving || !form.weight_kg || !form.height_cm || !form.waist_cm} className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-white" data-testid="bc-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Запиши измерване
          </Button>
          <p className="text-[10px] opacity-60 mt-2">
            * Body Fat % изисква Шия. За жени: + Ханш. Без тях ще се покажат само BMI + VAI.
          </p>
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, unit = "", band, delta, isDark, testid }) {
  const deltaColor = delta == null ? "" : delta > 0 ? "text-rose-500" : "text-emerald-500";
  return (
    <div className={`p-2 rounded-md ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`} data-testid={testid}>
      <div className="text-[9px] uppercase font-bold opacity-60">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value ?? "—"}{unit}</div>
      <div className="flex items-center gap-1 text-[10px] opacity-70">
        {band && <span>{band}</span>}
        {delta != null && delta !== 0 && (
          <span className={deltaColor}>{delta > 0 ? "+" : ""}{delta}</span>
        )}
      </div>
    </div>
  );
}
